import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useVatIntakePrices } from '../lib/useVatIntakePrices'
import VatIntakeTotals from './VatIntakeTotals'
import { supabase } from '../lib/supabase'
import { JOB_STATUS_OPTIONS } from '../lib/utils'
import { isSuspensionService, jobServicePriceLabel } from '../lib/serviceMenu'
import { runServiceJobCreateWithSchedule } from '../lib/jobLifecycle'

import CustomerCombobox from './CustomerCombobox'
import BikeFields, { EMPTY_BIKE, hasBikeDetails } from './BikeFields'
import { loadWorkshopRows } from '../lib/customerDirectory'

const TODAY = new Date().toISOString().split('T')[0]

const EMPTY_CUSTOMER = { name: '', email: '', phone: '' }
const EMPTY_COMPONENT = { type: 'fork', brand: 'rockshox', model: '', year: '', serial: '', notes: '' }
const EMPTY_JOB = { date: TODAY, scheduled_date: '', due_date: '', physical_location: '', requested_work: '', complaint: '', notes: '', status: 'received' }

function splitName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { first_name: '', last_name: '' }
  if (parts.length === 1) return { first_name: parts[0], last_name: '' }
  return { first_name: parts.slice(0, -1).join(' '), last_name: parts[parts.length - 1] }
}

function hasComponentDetails(component) {
  return Boolean(component.model || component.year || component.serial || component.notes)
}

function componentModelPlaceholder(type) {
  if (type === 'brake') return 'SRAM Maven Ultimate, Shimano XT M8120...'
  if (type === 'dropper') return 'Reverb AXS, Transfer, OneUp V3...'
  if (type === 'shock') return 'Float X2, Super Deluxe Ultimate...'
  return 'ZEB Ultimate, Fox 38 Factory...'
}

function componentNotesPlaceholder(type) {
  if (type === 'brake') return 'Lever/caliper, front/rear/pair, rotor size, pad compound, hose/fluid notes.'
  if (type === 'dropper') return 'Drop, insertion length, lever/routing, return speed, cartridge notes.'
  return 'Condition, tune, setup or service notes.'
}

function money(value) {
  return Number(value || 0).toFixed(2)
}

function intakeServiceSnapshot(service = {}) {
  const price = Number(service.base_price || 0)
  return {
    id: service.id,
    module_key: service.module_key || 'suspension',
    category: service.category || '',
    category_label: service.category || '',
    name: service.name || '',
    description: service.description || '',
    base_price: price,
    pricing_mode: service.pricing_mode || 'fixed',
    booking_type: service.booking_type || 'direct_bookable',
    expected_labour_minutes: Number(service.expected_labour_minutes || 0),
    price_label: service.pricing_mode === 'quote_required'
      ? 'Quote required'
      : service.pricing_mode === 'from'
        ? `From GBP ${price.toFixed(2)}`
        : `GBP ${price.toFixed(2)}`,
  }
}

export default function NewJobIntakeModal({
  workshopId,
  customers = [],
  bikes: initialBikes = [],
  components: initialComponents = [],
  services = [],
  preCustomerId = '',
  preBikeId = '',
  preComponentId = '',
  preServiceIds = [],
  preRequestedWork = '',
  returnLabel = 'Cancel',
  onClose,
  onSaved,
}) {
  const [bikes, setBikes] = useState(initialBikes)
  const [components, setComponents] = useState(initialComponents)
  const [chosenCustomer, setChosenCustomer] = useState(null)
  const [equipmentError, setEquipmentError] = useState('')
  const [equipmentRetry, setEquipmentRetry] = useState(0)
  const workshopRef = useRef(workshopId)
  const preComponent = components.find(component => component.id === preComponentId)
  const initialCustomerId = preCustomerId || preComponent?.customer_id || ''
  const requestedBikeId = preComponent?.bike_id || preBikeId
  const initialBikeId = bikes.some(bike => bike.id === requestedBikeId && bike.customer_id === initialCustomerId)
    ? requestedBikeId
    : ''
  const initialServiceIds = preServiceIds.filter(id => {
    const service = services.find(row => row.id === id)
    if (!service || service.active === false || !isSuspensionService(service)) return false
    if (!preComponent) return true
    const typeMatches = !preComponent.type || service.category === preComponent.type || service.category === 'combined' || service.category === 'other'
    const brandMatches = service.brand === 'any' || !preComponent.brand || service.brand === preComponent.brand
    return typeMatches && brandMatches
  })

  const [step, setStep] = useState(1)
  const [customerMode, setCustomerMode] = useState('existing')
  const [customerId, setCustomerId] = useState(initialCustomerId)
  const [customerForm, setCustomerForm] = useState(EMPTY_CUSTOMER)
  const [bikeMode, setBikeMode] = useState(initialBikeId ? 'existing' : 'none')
  const [bikeId, setBikeId] = useState(initialBikeId)
  const [bikeForm, setBikeForm] = useState(EMPTY_BIKE)
  const [componentMode, setComponentMode] = useState(preComponentId ? 'existing' : 'none')
  const [componentId, setComponentId] = useState(preComponentId)
  const [componentForm, setComponentForm] = useState(EMPTY_COMPONENT)
  const [jobForm, setJobForm] = useState(() => ({ ...EMPTY_JOB, requested_work: preRequestedWork }))
  const [selectedServices, setSelectedServices] = useState(initialServiceIds)
  const confirmedIntakeRecords = useRef(new Map())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (workshopRef.current !== workshopId) {
      workshopRef.current = workshopId
      useExistingCustomer('')
      setBikes([])
      setComponents([])
      setSelectedServices([])
    }
  }, [workshopId])

  useEffect(() => {
    if (!workshopId || !customerId || customerMode !== 'existing') return undefined
    const controller = new AbortController()
    setEquipmentError('')
    // Fetch all equipment pages scoped to this customer and workshop.
    // This also reaches bikes/components absent from the parent page's first load.
    Promise.all(['bikes', 'components'].map(async table => loadWorkshopRows(supabase, table, workshopId, '*', controller.signal, customerId)))
      .then(([bikeRows, componentRows]) => {
        if (controller.signal.aborted) return
        setBikes(bikeRows.filter(row => row.customer_id === customerId))
        setComponents(componentRows.filter(row => row.customer_id === customerId))
      }).catch(() => { if (!controller.signal.aborted) setEquipmentError('Could not refresh customer equipment.') })
    return () => controller.abort()
  }, [workshopId, customerId, customerMode, equipmentRetry])

  const activeServices = services.filter(service => service.active !== false && isSuspensionService(service))
  const selectedCustomerId = customerMode === 'existing' ? customerId : ''
  const selectedBikeId = bikeMode === 'existing' ? bikeId : ''
  const selectedComponentId = componentMode === 'existing' ? componentId : ''
  const customerBikes = bikes.filter(bike => bike.customer_id === selectedCustomerId)
  const customerComponents = components.filter(component => {
    if (component.customer_id !== selectedCustomerId) return false
    if (selectedBikeId && component.bike_id && component.bike_id !== selectedBikeId) return false
    return true
  })

  const duplicateCustomers = useMemo(() => {
    const email = customerForm.email.trim().toLowerCase()
    const phone = customerForm.phone.replace(/\s+/g, '')
    if (!email && !phone) return []
    return customers.filter(customer => {
      const customerEmail = String(customer.email || '').trim().toLowerCase()
      const customerPhone = String(customer.phone || '').replace(/\s+/g, '')
      return (email && customerEmail === email) || (phone && customerPhone === phone)
    })
  }, [customerForm.email, customerForm.phone, customers])

  const relevantServices = activeServices.filter(service => {
    const componentType = componentMode === 'existing'
      ? components.find(component => component.id === componentId)?.type
      : componentMode === 'new'
        ? componentForm.type
        : ''
    const componentBrand = componentMode === 'existing'
      ? components.find(component => component.id === componentId)?.brand
      : componentMode === 'new'
        ? componentForm.brand
        : ''

    return (!componentType || service.category === componentType || service.category === 'combined' || service.category === 'other')
      && (service.brand === 'any' || !componentBrand || service.brand === componentBrand)
  })

  const selectedServiceRows = activeServices.filter(service => selectedServices.includes(service.id))
  const vatIntake = useVatIntakePrices(workshopId, 'suspension', selectedServices)
  const estimatedTotal = vatIntake.gross ?? selectedServiceRows.reduce((sum, service) => sum + Number(service.base_price || 0), 0)
  const serviceSummary = selectedServiceRows.map(service => {
    const price = Number(service.base_price || 0)
    return `${service.name}${price > 0 ? ` - GBP ${price.toFixed(2)}` : ''}`
  }).join('\n')
  const selectedCustomer = selectedCustomerId ? (chosenCustomer?.id === selectedCustomerId ? chosenCustomer : customers.find(customer => customer.id === selectedCustomerId)) : null
  const selectedBike = selectedBikeId ? bikes.find(bike => bike.id === selectedBikeId) : null
  const selectedComponent = selectedComponentId ? components.find(component => component.id === selectedComponentId) : null
  const reviewCustomer = selectedCustomer
    ? [selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ')
    : customerForm.name.trim() || 'New customer'
  const reviewBike = selectedBike
    ? [selectedBike.year, selectedBike.brand, selectedBike.model].filter(Boolean).join(' ') || 'Bike record'
    : bikeMode === 'new' && hasBikeDetails(bikeForm)
      ? [bikeForm.year, bikeForm.brand, bikeForm.model].filter(Boolean).join(' ') || 'New bike details'
      : 'No bike linked'
  const reviewComponent = selectedComponent
    ? [selectedComponent.year, selectedComponent.brand, selectedComponent.model].filter(Boolean).join(' ') || 'Component record'
    : componentMode === 'new' && hasComponentDetails(componentForm)
      ? [componentForm.year, componentForm.brand, componentForm.model].filter(Boolean).join(' ') || 'New component details'
      : 'No component linked'
  const reviewStatus = JOB_STATUS_OPTIONS.find(([value]) => value === jobForm.status)?.[1] || jobForm.status
  const hasCustomer = customerMode === 'existing' ? Boolean(customerId) : Boolean(customerForm.name.trim())
  const hasJobRequest = Boolean(selectedServices.length || jobForm.requested_work.trim())

  function setCustomerField(field, value) {
    setCustomerForm(form => ({ ...form, [field]: value }))
  }

  function setBikeField(field, value) {
    setBikeForm(form => ({ ...form, [field]: value }))
  }

  function setComponentField(field, value) {
    setComponentForm(form => ({ ...form, [field]: value }))
  }

  function setJobField(field, value) {
    setJobForm(form => ({ ...form, [field]: value }))
  }

  function useExistingCustomer(id, row = null) {
    setChosenCustomer(row)
    setSelectedServices([])
    setCustomerMode('existing')
    setCustomerId(id)
    setBikeMode('none')
    setBikeId('')
    setComponentMode('none')
    setComponentId('')
  }

  function toggleService(service) {
    setSelectedServices(current => {
      const checked = current.includes(service.id)
      if (checked) return current.filter(id => id !== service.id)
      if (!service.is_addon) {
        const baseIds = relevantServices.filter(item => !item.is_addon).map(item => item.id)
        return [...current.filter(id => !baseIds.includes(id)), service.id]
      }
      return [...current, service.id]
    })
  }

  function canCreateJob() {
    return hasCustomer && hasJobRequest
  }

  function missingCreateRequirements() {
    const missing = []
    if (!hasCustomer) missing.push('customer - select an existing customer or enter a new customer name')
    if (!hasJobRequest) missing.push('work - select at least one service or enter requested work')
    return missing
  }

  function createRequirementsMessage() {
    const missing = missingCreateRequirements()
    if (!missing.length) return ''
    return `Create Job needs: ${missing.join('; ')}.`
  }

  async function logAudit(action, metadata = {}) {
    try {
      await supabase.from('audit_events').insert({
        workshop_id: workshopId,
        entity_type: 'job_intake',
        action,
        metadata,
      })
    } catch {}
  }

  async function insertIntakeRecord(table, payload) {
    const key = JSON.stringify([table, payload])
    if (vatIntake.prices && confirmedIntakeRecords.current.has(key)) return confirmedIntakeRecords.current.get(key)
    const result = await supabase.from(table).insert(payload).select('*').single()
    // Reuse confirmed customer/equipment IDs after an uncertain job response.
    // An unconfirmed customer insert still needs the existing duplicate review.
    if (vatIntake.prices && !result.error && result.data?.id) confirmedIntakeRecords.current.set(key, result)
    return result
  }

  async function save() {
    if (vatIntake.blocked) { setError(vatIntake.error || 'Wait for reviewed prices.'); return }
    if (!canCreateJob()) {
      setError(createRequirementsMessage())
      return
    }

    setSaving(true)
    setError(null)

    try {
      let finalCustomerId = customerId
      let finalBikeId = bikeMode === 'existing' ? bikeId || null : null
      let finalComponentId = componentMode === 'existing' ? componentId || null : null

      if (customerMode === 'new') {
        const name = splitName(customerForm.name)
        const { data, error: customerError } = await insertIntakeRecord('customers', {
            workshop_id: workshopId,
            ...name,
            email: customerForm.email.trim(),
            phone: customerForm.phone.trim(),
          })
        if (customerError) throw customerError
        finalCustomerId = data.id
        await logAudit('intake.customer_created', { customer_id: data.id })
      }

      if (bikeMode === 'new' && hasBikeDetails(bikeForm)) {
        const { data, error: bikeError } = await insertIntakeRecord('bikes', {
            workshop_id: workshopId,
            customer_id: finalCustomerId,
            ...bikeForm,
          })
        if (bikeError) throw bikeError
        finalBikeId = data.id
        await logAudit('intake.bike_created', { bike_id: data.id, customer_id: finalCustomerId })
      }

      if (componentMode === 'new' && hasComponentDetails(componentForm)) {
        const { data, error: componentError } = await insertIntakeRecord('components', {
            workshop_id: workshopId,
            customer_id: finalCustomerId,
            bike_id: finalBikeId,
            type: componentForm.type,
            brand: componentForm.brand,
            model: componentForm.model.trim() || 'Unidentified component',
            year: componentForm.year.trim(),
            serial: componentForm.serial.trim(),
            notes: componentForm.notes.trim(),
          })
        if (componentError) throw componentError
        finalComponentId = data.id
        await logAudit('intake.component_created', { component_id: data.id, customer_id: finalCustomerId, bike_id: finalBikeId })
      }

      if (componentMode === 'existing') {
        const existing = components.find(component => component.id === componentId)
        finalComponentId = componentId || null
        if (!finalBikeId && existing?.bike_id) finalBikeId = existing.bike_id
      }

      const { data: invoiceNumber } = vatIntake.prices ? { data: null } : await supabase.rpc('generate_invoice_number', { p_workshop_id: workshopId })
      const notes = [
        jobForm.requested_work ? `Requested work: ${jobForm.requested_work}` : null,
        jobForm.complaint ? `Customer complaint: ${jobForm.complaint}` : null,
        jobForm.notes ? `Intake notes: ${jobForm.notes}` : null,
      ].filter(Boolean).join('\n')

      const createResult = await runServiceJobCreateWithSchedule(supabase, {
        workshop_id: workshopId,
        customer_id: finalCustomerId,
        bike_id: finalBikeId,
        component_id: finalComponentId,
        date: jobForm.date || TODAY,
        due_date: jobForm.due_date || null,
        status: jobForm.status,
        physical_location: jobForm.physical_location.trim(),
        invoice_number: invoiceNumber || null,
        service_ids: selectedServices,
        service_summary: vatIntake.prices ? selectedServiceRows.map(s => s.name).join(', ') : serviceSummary,
        service_snapshot: selectedServiceRows.map(intakeServiceSnapshot),
        labour_charge: estimatedTotal,
        total_charged: estimatedTotal,
        notes,
      }, {
        ...vatIntake.options,
        scheduledDate: jobForm.scheduled_date || null,
        technicianId: null,
        overrideOverCapacity: false,
        note: 'Suspension job initial schedule set from intake.',
      })
      const scheduledJob = createResult?.job
      if (!scheduledJob?.id) throw new Error('Job was not returned after scheduling.')

      await logAudit('intake.job_created', {
        job_id: scheduledJob.id,
        customer_id: finalCustomerId,
        bike_id: finalBikeId,
        component_id: finalComponentId,
        status: scheduledJob.status,
      })

      onSaved(scheduledJob)
    } catch (err) {
      setError(err.message || 'Could not create job.')
    } finally {
      setSaving(false)
    }
  }

  const steps = [
    ['Intake', 1],
    ['Bike (Optional)', 2],
    ['Component', 3],
    ['Review', 4],
  ]

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide quick-intake-modal">
        <div className="modal-header quick-intake-header">
          <div>
            <div className="refresh-kicker">Quick intake</div>
            <div className="modal-title">Customer and requested work</div>
            <div className="text-muted fs12" style={{ marginTop: 3 }}>Suspension intake reuses known information; equipment details can be completed later.</div>
          </div>
          <button className="close-btn" aria-label="Close Suspension intake" onClick={onClose}>x</button>
        </div>

        <div className="quick-intake-steps">
          {steps.map(([label, value]) => (
            <button
              key={label}
              className={`btn ${step === value ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setStep(value)}
              type="button"
            >
              {value}. {label}
            </button>
          ))}
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}

        {step === 1 && (
          <>
            <div className="sec" style={{ marginTop: 0 }}>Customer</div>
            <div className="grid-2">
              <label className="check-item">
                <input type="radio" checked={customerMode === 'existing'} onChange={() => useExistingCustomer('')} />
                Existing customer
              </label>
              <label className="check-item">
                <input type="radio" checked={customerMode === 'new'} onChange={() => { useExistingCustomer(''); setCustomerMode('new') }} />
                New customer
              </label>
            </div>

            {customerMode === 'existing' ? (
              <div className="field">
                <CustomerCombobox workshopId={workshopId} value={customerId} selectedCustomer={selectedCustomer}
                  onChange={useExistingCustomer} disabled={saving} />
              </div>
            ) : (
              <>
                <div className="grid-3">
                  <div className="field"><label>Full Name</label><input value={customerForm.name} onChange={e => setCustomerField('name', e.target.value)} autoFocus /></div>
                  <div className="field"><label>Email</label><input type="email" value={customerForm.email} onChange={e => setCustomerField('email', e.target.value)} /></div>
                  <div className="field"><label>Phone</label><input value={customerForm.phone} onChange={e => setCustomerField('phone', e.target.value)} /></div>
                </div>
                {duplicateCustomers.length > 0 && (
                  <div className="job-modal-callout">
                    <div className="job-modal-callout-title">Possible Existing Customer</div>
                    {duplicateCustomers.map(customer => (
                      <div key={customer.id} className="job-row">
                        <div>
                          <div className="fs12 text-white fw7">{customer.first_name} {customer.last_name}</div>
                          <div className="fs12 text-muted">{customer.email || customer.phone}</div>
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => useExistingCustomer(customer.id)}>Use customer</button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {selectedCustomerId && (customerBikes.length > 0 || customerComponents.length > 0) && (
              <details className="intake-details">
                <summary>Link known bike/component</summary>
                <div className="grid-2" style={{ marginTop: 12 }}>
                  <div className="field">
                    <label>Bike</label>
                    <select value={selectedBikeId} onChange={e => {
                      setBikeId(e.target.value)
                      setBikeMode(e.target.value ? 'existing' : 'none')
                      setComponentId(''); setComponentMode('none')
                    }}>
                      <option value="">No bike selected</option>
                      {customerBikes.map(bike => (
                        <option key={bike.id} value={bike.id}>{[bike.year, bike.brand, bike.model].filter(Boolean).join(' ') || 'Bike record'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Component</label>
                    <select value={selectedComponentId} onChange={e => {
                      const nextComponent = components.find(component => component.id === e.target.value)
                      setComponentId(e.target.value)
                      setComponentMode(e.target.value ? 'existing' : 'none')
                      if (nextComponent?.bike_id) {
                        setBikeId(nextComponent.bike_id)
                        setBikeMode('existing')
                      }
                    }}>
                      <option value="">No component selected</option>
                      {customerComponents.map(component => (
                        <option key={component.id} value={component.id}>
                          {[component.year, component.brand, component.model].filter(Boolean).join(' ')}{component.serial ? ` (${component.serial})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </details>
            )}
            {equipmentError && <div role="alert" className="alert alert-error">{equipmentError} <button type="button" className="btn btn-ghost" onClick={() => setEquipmentRetry(n => n + 1)}>Retry equipment</button></div>}
            <div className="text-muted fs12">Intended bike: {reviewBike}</div>
            <div className="sec">Job essentials</div>
            <div className="grid-3">
              <div className="field"><label>Received Date</label><input type="date" value={jobForm.date} onChange={e => setJobField('date', e.target.value)} /></div>
              <div className="field"><label>Date Booked In For</label><input type="date" value={jobForm.scheduled_date} onChange={e => setJobField('scheduled_date', e.target.value)} /></div>
              <div className="field"><label>Date For Collection / Returned</label><input type="date" value={jobForm.due_date} onChange={e => setJobField('due_date', e.target.value)} /></div>
              <div className="field"><label>Initial Status</label>
                <select value={jobForm.status} onChange={e => setJobField('status', e.target.value)}>
                  {JOB_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Physical Location</label>
              <input value={jobForm.physical_location} onChange={e => setJobField('physical_location', e.target.value)} placeholder="Rack A, bench 2, awaiting wash..." />
            </div>

            {relevantServices.length > 0 && (
              <div className="job-modal-callout">
                <div className="job-modal-callout-title">Service Type</div>
                <div className="text-muted fs12" style={{ marginBottom: 10 }}>Choose a service if known. If not, add requested work below.</div>
                {relevantServices.map(service => {
                  const checked = selectedServices.includes(service.id)
                  return (
                    <label key={service.id} className="check-item" style={{ marginBottom: 6 }} onClick={() => toggleService(service)}>
                      <input type="checkbox" checked={checked} onChange={() => {}} />
                      <span style={{ flex: 1 }}>{service.name}</span>
                      <span className="text-green fw7">{jobServicePriceLabel(service, vatIntake)}</span>
                    </label>
                  )
                })}
              </div>
            )}

            <div className="grid-2">
              <div className="field"><label>Requested Work</label><textarea value={jobForm.requested_work} onChange={e => setJobField('requested_work', e.target.value)} placeholder="Full service, inspection, setup..." /></div>
              <div className="field"><label>Customer Complaint</label><textarea value={jobForm.complaint} onChange={e => setJobField('complaint', e.target.value)} placeholder="Noise, leak, poor rebound, no lockout..." /></div>
            </div>
            <div className="field"><label>Internal Intake Notes</label><textarea value={jobForm.notes} onChange={e => setJobField('notes', e.target.value)} /></div>
            <div className="detail-row">
              <span className="detail-label">{vatIntake.prices ? 'Total (including any VAT)' : 'Estimated Total'}</span>
              <span className="detail-val text-white fw7">GBP {money(estimatedTotal)}</span>
            </div>
          </>
        )}

        <VatIntakeTotals intake={vatIntake} />
        {step === 2 && (
          <>
            <div className="sec" style={{ marginTop: 0 }}>Bike (Optional)</div>
            <div className="text-muted fs12" style={{ marginBottom: 12 }}>
              You can create the job without a bike and link one later.
            </div>
            <div className="grid-3">
              <label className="check-item">
                <input type="radio" checked={bikeMode === 'existing'} onChange={() => setBikeMode('existing')} disabled={!selectedCustomerId || !customerBikes.length} />
                Existing bike
              </label>
              <label className="check-item">
                <input type="radio" checked={bikeMode === 'new'} onChange={() => setBikeMode('new')} />
                New bike
              </label>
              <label className="check-item">
                <input type="radio" checked={bikeMode === 'none'} onChange={() => { setBikeMode('none'); setBikeId('') }} />
                Skip bike
              </label>
            </div>

            {bikeMode === 'existing' ? (
              <div className="field">
                <label>Bike</label>
                <select value={bikeId} onChange={e => { setBikeId(e.target.value); setComponentId(''); setComponentMode('none') }}>
                  <option value="">Select bike...</option>
                  {customerBikes.map(bike => (
                    <option key={bike.id} value={bike.id}>{[bike.year, bike.brand, bike.model].filter(Boolean).join(' ') || 'Bike record'}</option>
                  ))}
                </select>
              </div>
            ) : bikeMode === 'new' ? (
              <BikeFields form={bikeForm} onChange={setBikeField} disabled={saving} />
            ) : (
              <div className="muted-empty">No bike will be linked. The job can still be created.</div>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <div className="sec" style={{ marginTop: 0 }}>Suspension Component</div>
            <div className="grid-3">
              <label className="check-item">
                <input type="radio" checked={componentMode === 'existing'} onChange={() => setComponentMode('existing')} disabled={!selectedCustomerId || !customerComponents.length} />
                Existing component
              </label>
              <label className="check-item">
                <input type="radio" checked={componentMode === 'new'} onChange={() => setComponentMode('new')} />
                New component
              </label>
              <label className="check-item">
                <input type="radio" checked={componentMode === 'none'} onChange={() => { setComponentMode('none'); setComponentId('') }} />
                Skip component
              </label>
            </div>

            {componentMode === 'existing' ? (
              <div className="field">
                <label>Component</label>
                <select value={componentId} onChange={e => setComponentId(e.target.value)}>
                  <option value="">Select component...</option>
                  {customerComponents.map(component => (
                    <option key={component.id} value={component.id}>
                      {[component.year, component.brand, component.model].filter(Boolean).join(' ')}{component.serial ? ` (${component.serial})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : componentMode === 'new' ? (
              <>
                <div className="grid-2">
                  <div className="field"><label>Type</label>
                    <select value={componentForm.type} onChange={e => setComponentField('type', e.target.value)}>
                      <option value="fork">Fork</option>
                      <option value="shock">Rear Shock</option>
                      <option value="dropper">Dropper Post</option>
                      <option value="brake">Brake</option>
                      <option value="drivetrain">Drivetrain</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="field"><label>Brand</label>
                    <select value={componentForm.brand} onChange={e => setComponentField('brand', e.target.value)}>
                      <option value="rockshox">RockShox</option>
                      <option value="fox">Fox</option>
                      <option value="ohlins">Ohlins</option>
                      <option value="marzocchi">Marzocchi</option>
                      <option value="sram">SRAM</option>
                      <option value="shimano">Shimano</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="grid-3">
                  <div className="field"><label>{componentForm.type === 'brake' ? 'Lever / Caliper Model' : 'Model'}</label><input value={componentForm.model} onChange={e => setComponentField('model', e.target.value)} placeholder={componentModelPlaceholder(componentForm.type)} /></div>
                  <div className="field"><label>Year</label><input value={componentForm.year} onChange={e => setComponentField('year', e.target.value)} /></div>
                  <div className="field"><label>{componentForm.type === 'brake' ? 'Serial / Caliper Code' : 'Serial Number'}</label><input className="mono" value={componentForm.serial} onChange={e => setComponentField('serial', e.target.value)} /></div>
                </div>
                <details>
                  <summary className="text-muted fs12 fw7">Advanced component details</summary>
                  <div className="field" style={{ marginTop: 12 }}>
                    <label>{componentForm.type === 'brake' ? 'Brake Notes' : 'Component Notes'}</label>
                    <textarea value={componentForm.notes} onChange={e => setComponentField('notes', e.target.value)} placeholder={componentNotesPlaceholder(componentForm.type)} />
                  </div>
                </details>
              </>
            ) : (
              <div className="muted-empty">A draft job can be created without a component and linked later.</div>
            )}
          </>
        )}

        {step === 4 && (
          <>
            <div className="sec" style={{ marginTop: 0 }}>Review</div>
            <div className="job-modal-callout">
              <div className="job-modal-callout-title">Confirm Intake Details</div>
              <div className="detail-row">
                <span className="detail-label">Customer</span>
                <span className="detail-val text-white fw7">{reviewCustomer}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Bike</span>
                <span className="detail-val text-white fw7">{reviewBike}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Component</span>
                <span className="detail-val text-white fw7">{reviewComponent}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Received Date</span>
                <span className="detail-val text-white fw7">{jobForm.date || TODAY}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date Booked In For</span>
                <span className="detail-val text-white fw7">{jobForm.scheduled_date || 'Not scheduled'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date For Collection / Returned</span>
                <span className="detail-val text-white fw7">{jobForm.due_date || 'Not set'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Initial Status</span>
                <span className="detail-val text-white fw7">{reviewStatus}</span>
              </div>
            </div>

            <div className="job-modal-callout">
              <div className="job-modal-callout-title">Work Summary</div>
              {selectedServiceRows.length > 0 ? selectedServiceRows.map(service => (
                <div key={service.id} className="detail-row">
                  <span className="detail-label">{service.name}</span>
                  <span className="detail-val text-green fw7">{jobServicePriceLabel(service, vatIntake)}</span>
                </div>
              )) : (
                <div className="detail-row">
                  <span className="detail-label">Service</span>
                  <span className="detail-val text-white fw7">Not selected</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Requested Work</span>
                <span className="detail-val text-white fw7">{jobForm.requested_work.trim() || 'Not supplied'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Customer Complaint</span>
                <span className="detail-val text-white fw7">{jobForm.complaint.trim() || 'Not supplied'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Internal Notes</span>
                <span className="detail-val text-white fw7">{jobForm.notes.trim() || 'Not supplied'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{vatIntake.prices ? 'Total (including any VAT)' : 'Estimated Total'}</span>
                <span className="detail-val text-white fw7">GBP {money(estimatedTotal)}</span>
              </div>
            </div>
          </>
        )}

        <div className="modal-action-bar">
          <div className="modal-action-bar-inner">
            <div className="action-row">
              <button className="btn btn-ghost" onClick={onClose} disabled={saving}>{returnLabel}</button>
              {step > 1 && <button className="btn btn-ghost" onClick={() => setStep(current => Math.max(1, current - 1))} disabled={saving}>Back</button>}
              {step === 1 ? (
                <>
                  <button className="btn btn-ghost" onClick={() => {
                    setError(null)
                    setStep(2)
                  }} disabled={saving}>
                    Next
                  </button>
                  <button className="btn btn-primary" onClick={save} disabled={saving || vatIntake.blocked}>
                    {saving ? 'Creating...' : 'Create Job'}
                  </button>
                </>
              ) : step < 4 ? (
                <button className="btn btn-primary" onClick={() => {
                  setError(null)
                  setStep(current => Math.min(4, current + 1))
                }} disabled={saving}>
                  Next
                </button>
              ) : (
                <button className="btn btn-primary" onClick={save} disabled={saving || vatIntake.blocked}>
                  {saving ? 'Creating...' : 'Create Job'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
